

import React, { useState, useEffect, useRef } from 'react';
import {
  Modal,
  Box,
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Autocomplete,
  CircularProgress,
  InputAdornment,
  Chip,
  IconButton,
} from '@mui/material';
import { createFilterOptions } from '@mui/material/Autocomplete';
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import { validatePersonOrEntityName } from "utils/crmValidation";
import MDButton from "components/MDButton";
import CloseIcon from '@mui/icons-material/Close';
import { use } from 'react';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';
import colors from "assets/theme/base/colors";
import CustomFieldsSection from 'components/CustomFields/CustomFieldsSection';
import { CountrySelect, StateSelect, CitySelect } from 'components/GeographySelect';
// import axiosInstance from 'utils/axiosInstance';
import { useAuth } from 'auth/Auth';
import { useCurrency } from '../../context/CurrencyContext';
import { useTravelUi } from 'hooks/useTravelUi';
import { frontendLogger } from 'utils/logger';
import AddressAutocompleteInput from "../../layouts/Travel/trips/stepForm/fields/AddressAutocompleteInput";
import { useCountryCodes } from 'hooks/useCountryCodes';
import {
  composeStoredPhone,
  dialCodeForCountryName,
  nationalDigits,
  phoneLengthHint,
  splitStoredPhone,
} from 'utils/accountPhone';


const DEFAULT_ACCOUNT_FORM = {
  account_type: 'Company',
  account_status: 'Active',
  partner_type: '',
  service_type: [],
  trading_name: '',
  business_registration_number: '',
  country_of_registration: '',
  billing_country: '',
  shipping_country: '',
  billing_state: '',
  billing_city: '',
  shipping_state: '',
  shipping_city: '',
  name: '',
  email: '',
  phone: '',
  mobile: '',
};

const countryCodeFilter = createFilterOptions({
  stringify: (option) => `${option.dialCode} ${option.country} ${option.iso2}`,
});

function AccountPhoneInput({
  label,
  required,
  dialCode,
  national,
  countryCodes,
  getByDialCode,
  getPhoneLengthRange,
  error,
  extraHelper,
  onDialChange,
  onNationalChange,
}) {
  const range = dialCode ? getPhoneLengthRange({ dialCode }) : null;
  const maxLength = range?.maxLength || 15;
  const selected = (dialCode && getByDialCode(dialCode)) || null;

  return (
    <MDBox display="flex" gap={1} alignItems="flex-start">
      <Autocomplete
        options={countryCodes}
        filterOptions={countryCodeFilter}
        value={selected}
        onChange={(_, value) => onDialChange(value?.dialCode || '')}
        getOptionLabel={(option) => option?.dialCode || ''}
        isOptionEqualToValue={(option, value) => option?.dialCode === value?.dialCode}
        renderOption={(props, option) => (
          <MDBox component="li" {...props} key={`${option.iso2}-${option.dialCode}`} display="flex" gap={1}>
            <MDTypography variant="caption">{option.dialCode}</MDTypography>
            <MDTypography variant="caption" color="text">{option.country}</MDTypography>
          </MDBox>
        )}
        slotProps={{ popper: { sx: { width: '280px !important' } } }}
        sx={{ width: 148, flexShrink: 0 }}
        renderInput={(params) => (
          <TextField
            {...params}
            label="Code"
            margin="normal"
            required={required}
            error={!!error}
            placeholder="+91"
          />
        )}
      />
      <TextField
        fullWidth
        name={label}
        label={label}
        variant="outlined"
        margin="normal"
        required={required}
        value={national}
        error={!!error}
        helperText={error || extraHelper || phoneLengthHint(range)}
        inputProps={{ inputMode: 'numeric', maxLength, pattern: '[0-9]*' }}
        onChange={(event) => onNationalChange(nationalDigits(event.target.value, maxLength))}
      />
    </MDBox>
  );
}

const buildAccountFormData = (initialData) => {
  if (!initialData) return { ...DEFAULT_ACCOUNT_FORM };
  const {
    id,
    created_by,
    updated_by,
    created_by_name,
    updated_by_name,
    created_at,
    updated_at,
    custom_fields,
    ...rest
  } = initialData;
  const cfFlat =
    custom_fields && typeof custom_fields === 'object'
      ? custom_fields
      : Object.fromEntries(
          Object.entries(initialData).filter(([key]) => key.startsWith('cf_') || /^cf\d+$/.test(key))
        );
  return {
    ...DEFAULT_ACCOUNT_FORM,
    ...rest,
    ...cfFlat,
    account_type: rest.account_type || 'Company',
    account_status: rest.account_status || 'Active',
    partner_type: rest.partner_type || '',
    service_type: Array.isArray(rest.service_type) ? rest.service_type : [],
    name: rest.name || '',
    email: rest.email || '',
    phone: rest.phone || '',
    mobile: rest.mobile || '',
  };
};

const AccountFormModal = ({
  isOpen,
  onClose,
  onSave,
  managers,
  currencies,
  countries,
  loadingCountries,
  defaultCurrency,
  user,
  title = 'Add New Account',
  initialData = null,
}) => {
  const { showTravel } = useTravelUi();
  const accountTypeOptions = showTravel
    ? ["Company", "Partner", "Individual"]
    : ["Company", "Individual"];
  const isClone = Boolean(initialData);
  const [formData, setFormData] = useState(() => buildAccountFormData(initialData));
  const { countryCodes, getByDialCode, getPhoneLengthRange, isValidPhoneNumber } = useCountryCodes();
  const [phoneDial, setPhoneDial] = useState('');
  const [phoneNational, setPhoneNational] = useState('');
  const [mobileDial, setMobileDial] = useState('');
  const [mobileNational, setMobileNational] = useState('');
  const phoneDialTouched = useRef(false);
  const mobileDialTouched = useRef(false);
  const {axiosInstance } = useAuth();
  const [selectedManager, setSelectedManager] = useState(null);
  const { currency: systemCurrency } = useCurrency(); // Add this line
  // const [currency, setCurrency] = useState(defaultCurrency);
  const [currency, setCurrency] = useState(null); // Initialize as null initially
  const [formErrors, setFormErrors] = useState({});
  const [selectedAgent, setSelectedAgent] = useState();
  const [isSaving, setIsSaving] = useState(false);
  const [customFields, setCustomFields] = useState([]);
  const customFieldsRef = useRef(null);

  const isPartner = formData.account_type === 'Partner';
  const isMerchant = isPartner && formData.partner_type === 'Merchant';
  const DEFAULT_PARTNER_TYPES = ["Merchant", "Guide", "Influencer", "Affiliate", "Other"];
  const DEFAULT_SERVICE_TYPES = ["Accommodation", "Transport", "Transfer", "Activity", "Meals", "Guide", "DMC"];
  const [partnerTypeOptions, setPartnerTypeOptions] = useState(DEFAULT_PARTNER_TYPES);
  const [serviceTypeOptions, setServiceTypeOptions] = useState(DEFAULT_SERVICE_TYPES);

  useEffect(() => {
    if (user) {
      const currentUserAsManager = {
        id: user.id,
        username: user.username
      };
      setSelectedManager(currentUserAsManager);
    }
  }, [user]);

  // useEffect(() => {
  //   if (isOpen) {
  //     setFormData({
  //       ...formData,
  //       account_status: 'Active',
  //     });
  //     setSelectedBillingCountry(null);
  //     setSelectedShippingCountry(null);
  //     setCurrency(defaultCurrency);
  //     setFormErrors({});
  //   }
  // }, [isOpen, defaultCurrency]);

  // Reset only when the modal opens. Do not depend on initialData — parents
  // often pass an inline object, which would wipe email/phone after a failed
  // save (matches ContactFormModal).
  useEffect(() => {
    if (!isOpen) return;
    const seed = buildAccountFormData(initialData);
    setFormData(seed);
    const phoneParts = splitStoredPhone(seed.phone, countryCodes);
    const mobileParts = splitStoredPhone(seed.mobile, countryCodes);
    const countryDial = dialCodeForCountryName(seed.billing_country, countryCodes);
    setPhoneDial(phoneParts.dialCode || countryDial);
    setPhoneNational(phoneParts.national);
    setMobileDial(mobileParts.dialCode || countryDial);
    setMobileNational(mobileParts.national);
    phoneDialTouched.current = Boolean(phoneParts.dialCode);
    mobileDialTouched.current = Boolean(mobileParts.dialCode);
    setFormErrors({});
    const currencyCode = seed.currency || systemCurrency;
    const defaultCurrencyObj =
      (currencies || []).find((c) => c.code === currencyCode) ||
      (currencies || []).find((c) => c.code === systemCurrency) ||
      currencies?.[0];
    setCurrency(defaultCurrencyObj || null);
    if (seed.manager_id) {
      const mgr =
        (managers || []).find((m) => m.id === seed.manager_id) ||
        { id: seed.manager_id, username: seed.manager_name || user?.username };
      setSelectedManager(mgr);
    } else if (user) {
      setSelectedManager({ id: user.id, username: user.username });
    }
  }, [isOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  // Add useEffect to fetch custom fields
  useEffect(() => {
    const fetchCustomFields = async () => {
      try {
        const response = await axiosInstance.get('/custom-fields/accounts');
        setCustomFields(response.data || []);
      } catch (error) {
        console.error('Error fetching custom fields:', error);
        setCustomFields([]);
      }
    };

    fetchCustomFields();
  }, []);

  // Load configurable partner/service type options (defaults + values saved on accounts)
  useEffect(() => {
    const fetchPartnerOptions = async () => {
      try {
        const response = await axiosInstance.get('/accounts/partner-options');
        setPartnerTypeOptions(response.data?.partner_types?.length ? response.data.partner_types : DEFAULT_PARTNER_TYPES);
        setServiceTypeOptions(response.data?.service_types?.length ? response.data.service_types : DEFAULT_SERVICE_TYPES);
      } catch (error) {
        console.error('Error fetching partner options:', error);
        setPartnerTypeOptions(DEFAULT_PARTNER_TYPES);
        setServiceTypeOptions(DEFAULT_SERVICE_TYPES);
      }
    };
    if (isOpen) {
      fetchPartnerOptions();
    }
  }, [isOpen, axiosInstance]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    // Trim the value for the Tax ID field
    const trimmedValue = name === 'tax_id' ? value.trim() : value;

    setFormData((prev) => {
      const next = { ...prev, [name]: trimmedValue };
      // Reset service type when leaving Merchant partner type
      if (name === 'partner_type' && trimmedValue !== 'Merchant') {
        next.service_type = [];
      }
      if (name === 'account_type' && trimmedValue !== 'Partner') {
        next.partner_type = '';
        next.service_type = [];
        next.trading_name = '';
        next.business_registration_number = '';
        next.country_of_registration = '';
      }
      return next;
    });
    setFormErrors({ ...formErrors, [name]: '' });
  };

  const handleSaveAccount = async (e) => {
    e.preventDefault();
    setIsSaving(true); // Start loading
    try {
      // Validate custom fields
      const isCustomFieldsValid = customFieldsRef.current?.validateAllFields() ?? true;
      
      if (validateForm() && isCustomFieldsValid) {
        const accountData = {
          ...formData,
          phone: composeStoredPhone(phoneDial, phoneNational),
          mobile: composeStoredPhone(mobileDial, mobileNational),
          manager_id: selectedManager?.id,
          manager_name: selectedManager?.username,
          currency: currency?.code,
        };

        if (formData.account_type === 'Partner') {
          accountData.country_of_registration = formData.country_of_registration || '';
          accountData.service_type = Array.isArray(formData.service_type) ? formData.service_type : [];
        } else {
          delete accountData.partner_type;
          delete accountData.service_type;
          delete accountData.trading_name;
          delete accountData.business_registration_number;
          delete accountData.country_of_registration;
        }

        // Empty strings fail Pydantic EmailStr / int / float (422). Coerce to null.
        ['email', 'website', 'phone', 'mobile', 'fax', 'tax_id', 'notes'].forEach((key) => {
          if (accountData[key] === '') accountData[key] = null;
        });
        if (accountData.annual_revenue === '' || accountData.annual_revenue == null) {
          accountData.annual_revenue = null;
        } else {
          accountData.annual_revenue = Number(accountData.annual_revenue);
        }
        ['number_of_employees', 'billing_stcd', 'shipping_stcd'].forEach((key) => {
          if (accountData[key] === '' || accountData[key] == null) {
            accountData[key] = null;
          } else {
            accountData[key] = parseInt(accountData[key], 10);
          }
        });

        const response = await onSave(accountData);

        // Log account creation
        if (response && response.id) {
          await frontendLogger.logCreate('Account', {
            id: response.id,
            name: response.name || formData.name,
            account_type: formData.account_type,
            account_status: formData.account_status,
            industry: formData.industry,
            email: formData.email,
            phone: accountData.phone,
            website: formData.website,
            manager_id: selectedManager?.id,
            manager_name: selectedManager?.username,
            created_by: user?.id,
            created_by_name: user?.username,
            timestamp: new Date().toISOString(),
            user: user?.username,
            action: 'create'
          });
        }

        onClose(); // Close modal only if save succeeds
      }
    } catch (error) {
      console.error('Error saving account:', error);

      // Log error
      await frontendLogger.logError(error, {
        context: 'Account creation',
        formData: formData,
        user: user?.username
      });

      const detail = error?.response?.data?.detail;
      if (typeof detail === 'string') {
        const lower = detail.toLowerCase();
        if (lower.includes('email')) {
          setFormErrors((prev) => ({ ...prev, email: detail }));
        }
        if (lower.includes('name') || lower.includes('already exists')) {
          setFormErrors((prev) => ({ ...prev, name: detail }));
        }
      }
    } finally {
      setIsSaving(false); // Stop loading regardless of success or failure
    }
  };

  const numberError = (label, dialCode, national, required) => {
    const digits = String(national || '').replace(/\D/g, '');
    if (!digits) return required ? `Enter ${label.toLowerCase()}.` : '';
    if (!dialCode) return `Select a country code for ${label.toLowerCase()}.`;
    if (!isValidPhoneNumber({ dialCode }, digits)) {
      const range = getPhoneLengthRange({ dialCode });
      const expect = range
        ? (range.minLength === range.maxLength
          ? `${range.minLength} digits`
          : `${range.minLength}–${range.maxLength} digits`)
        : 'a valid length';
      return `${label} must be ${expect} for this country.`;
    }
    return '';
  };

  const validateForm = () => {
    const errors = {};
    const partner = formData.account_type === 'Partner';
    const storedPhone = composeStoredPhone(phoneDial, phoneNational);

    if (!formData.name) {
      errors.name = partner ? 'Enter account name.' : 'Account name is required';
    } else {
      const nameErr = validatePersonOrEntityName(formData.name, 'Account name');
      if (nameErr) errors.name = nameErr;
    }

    if (partner && !(formData.trading_name || '').trim()) {
      errors.trading_name = 'Enter trading name.';
    } else if (formData.trading_name) {
      const tradingErr = validatePersonOrEntityName(formData.trading_name, 'Trading name');
      if (tradingErr) errors.trading_name = tradingErr;
    }

    if (partner) {
      if (!formData.partner_type) {
        errors.partner_type = 'Select partner type.';
      }
      if (formData.partner_type === 'Merchant' && (!formData.service_type || formData.service_type.length === 0)) {
        errors.service_type = 'Select at least one service type.';
      }
      if (!formData.country_of_registration) {
        errors.country_of_registration = 'Select a country.';
      }
      if (!formData.email) {
        errors.email = 'Enter business email.';
      }
      if (!storedPhone) {
        errors.phone = 'Enter business phone.';
      }
      if (!formData.billing_address) {
        errors.billing_address = 'Enter billing address.';
      }
      if (!currency?.code) {
        errors.currency = 'Select currency.';
      }
    } else if (isClone && !(formData.email || '').trim() && !storedPhone) {
      // Match Contact clone: email/phone are unique — require a new contact method.
      const msg = 'Either Email or Phone is required';
      errors.email = msg;
      errors.phone = msg;
    }

    if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      errors.email = partner ? 'Enter a valid email.' : 'Invalid email address';
    }

    const phoneErr = numberError('Phone', phoneDial, phoneNational, false);
    if (phoneErr && !errors.phone) errors.phone = phoneErr;

    if (formData.website && !/^(https?:\/\/)?([\da-z\.-]+)\.([a-z\.]{2,6})([\/\w \.-]*)*\/?$/.test(formData.website)) {
      errors.website = partner ? 'Enter a valid website.' : 'Invalid website URL';
    }

    const mobileErr = numberError('Mobile number', mobileDial, mobileNational, false);
    if (mobileErr) errors.mobile = mobileErr;

    if (formData.number_of_employees && (isNaN(formData.number_of_employees) || parseInt(formData.number_of_employees) < 0)) {
      errors.number_of_employees = 'Number of employees must be a non-negative integer';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleCurrencyChange = (_, newValue) => {
    setCurrency(newValue);
  };

  const handleCopyBillingToShipping = (isChecked) => {
    if (isChecked) {
      setFormData((prevData) => ({
        ...prevData,
        shipping_address: prevData.billing_address,
        shipping_city: prevData.billing_city,
        shipping_country: prevData.billing_country,
        shipping_state: prevData.billing_state,
        shipping_postal_code: prevData.billing_postal_code,
        shipping_stcd: prevData.billing_stcd,
      }));
    } else {
      setFormData((prevData) => ({
        ...prevData,
        shipping_address: '',
        shipping_city: '',
        shipping_country: '',
        shipping_state: '',
        shipping_postal_code: '',
        shipping_stcd: '',
      }));
    }
  };

  const handleFieldUpdate = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleFieldError = (field, error) => {
    if (error) {
      setFormErrors(prev => ({
        ...prev,
        [field]: error
      }));
    } else {
      setFormErrors(prev => {
        const { [field]: _, ...rest } = prev;
        return rest;
      });
    }
  };

  return (
    <Modal
      open={isOpen}
      onClose={onClose}
      aria-labelledby="account-form-modal-title"
    >
      <Box sx={{
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        width: { xs: '90%', sm: '80%', md: '70%', lg: '60%' },
        maxWidth: 800,
        maxHeight: '90vh',
        bgcolor: 'background.paper',
        boxShadow: 24,
        borderRadius: 2,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }}>
        {/* Header */}
        <Box className="pearl-popup-header"
          sx={{
            backgroundColor: colors.popupHeader.background,
            px: 4,
            py: 3,
            position: 'sticky',
            top: 0,
            zIndex: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <MDTypography
            id="account-form-modal-title"
            variant="h6"
            component="h2"
            sx={{ color: '#FFFFFF' }}
          >
            {title}
          </MDTypography>
          <IconButton
            aria-label="Close"
            onClick={onClose}
            sx={{ color: '#FFFFFF', ml: 1 }}
          >
            <CloseIcon />
          </IconButton>
        </Box>

        {/* Scrollable content area */}
        <Box
          sx={{
            overflowY: 'auto',
            p: 4,
            '&::-webkit-scrollbar': {
              width: '0.4em'
            },
            '&::-webkit-scrollbar-track': {
              background: '#f1f1f1',
              borderRadius: '4px'
            },
            '&::-webkit-scrollbar-thumb': {
              backgroundColor: '#888',
              borderRadius: '4px'
            },
            '&::-webkit-scrollbar-thumb:hover': {
              background: '#555'
            }
          }}
        >
          <form onSubmit={handleSaveAccount}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  name="name"
                  label="Account Name"
                  variant="outlined"
                  margin="normal"
                  required
                  value={formData.name || ''}
                  onChange={handleInputChange}
                  error={!!formErrors.name}
                  helperText={formErrors.name}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <FormControl fullWidth variant="outlined" margin="normal">
                  <InputLabel>Account Type</InputLabel>
                  <Select
                    name="account_type"
                    label="Account Type"
                    required
                    value={formData.account_type}
                    onChange={handleInputChange}

                  >
                    {accountTypeOptions.map((type) => (
                      <MenuItem key={type} value={type}>{type}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              {showTravel && isPartner && (
                <>
                  <Grid item xs={12}>
                    <MDTypography variant="h6" color="dark" mt={0.5}>
                      Partner Details
                    </MDTypography>
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <Autocomplete
                      freeSolo
                      options={partnerTypeOptions}
                      value={formData.partner_type || ''}
                      onChange={(_, newValue) => {
                        const value = (newValue || '').trim();
                        setFormData((prev) => ({
                          ...prev,
                          partner_type: value,
                          service_type: value !== 'Merchant' ? [] : prev.service_type,
                        }));
                        setFormErrors((prev) => ({ ...prev, partner_type: '' }));
                      }}
                      onInputChange={(_, newInputValue, reason) => {
                        if (reason === 'input') {
                          setFormData((prev) => ({
                            ...prev,
                            partner_type: newInputValue,
                            service_type: newInputValue !== 'Merchant' && prev.partner_type === 'Merchant'
                              ? []
                              : prev.service_type,
                          }));
                          setFormErrors((prev) => ({ ...prev, partner_type: '' }));
                        }
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Partner Type"
                          variant="outlined"
                          margin="normal"
                          required
                          error={!!formErrors.partner_type}
                          helperText={formErrors.partner_type || 'Select or type a new partner type'}
                          InputProps={{
                            ...params.InputProps,
                            style: { height: '45px' },
                          }}
                        />
                      )}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <Autocomplete
                      multiple
                      freeSolo
                      disabled={!isMerchant}
                      options={serviceTypeOptions}
                      value={formData.service_type || []}
                      onChange={(_, newValue) => {
                        const cleaned = (newValue || [])
                          .map((v) => (typeof v === 'string' ? v.trim() : v))
                          .filter(Boolean);
                        // de-dupe case-insensitive
                        const unique = [];
                        cleaned.forEach((v) => {
                          if (!unique.some((u) => u.toLowerCase() === v.toLowerCase())) {
                            unique.push(v);
                          }
                        });
                        setFormData((prev) => ({ ...prev, service_type: unique }));
                        setFormErrors((prev) => ({ ...prev, service_type: '' }));
                      }}
                      renderTags={(value, getTagProps) =>
                        value.map((option, index) => (
                          <Chip variant="outlined" label={option} size="small" {...getTagProps({ index })} />
                        ))
                      }
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label={isMerchant ? 'Service Type *' : 'Service Type'}
                          variant="outlined"
                          margin="normal"
                          error={!!formErrors.service_type}
                          helperText={
                            formErrors.service_type ||
                            (isMerchant
                              ? 'Select or type service types (required for Merchant)'
                              : 'Available when Partner Type is Merchant')
                          }
                          // Autocomplete multi-select keeps the underlying input empty when
                          // chips are selected; HTML5 required would falsely block submit.
                          inputProps={{
                            ...params.inputProps,
                            required: false,
                          }}
                        />
                      )}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      name="trading_name"
                      label="Trading Name"
                      variant="outlined"
                      margin="normal"
                      required
                      value={formData.trading_name || ''}
                      onChange={handleInputChange}
                      error={!!formErrors.trading_name}
                      helperText={formErrors.trading_name}
                      sx={{ '& .MuiFormLabel-asterisk': { color: 'error.main' } }}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      name="business_registration_number"
                      label="Business Registration Number"
                      variant="outlined"
                      margin="normal"
                      value={formData.business_registration_number || ''}
                      onChange={handleInputChange}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <CountrySelect
                      label="Country of Registration"
                      value={formData.country_of_registration || ''}
                      margin="normal"
                      required
                      error={!!formErrors.country_of_registration}
                      helperText={formErrors.country_of_registration}
                      onChange={(nextCountry) => {
                        setFormData((prev) => ({
                          ...prev,
                          country_of_registration: nextCountry || '',
                        }));
                        setFormErrors((prev) => ({ ...prev, country_of_registration: '' }));
                      }}
                    />
                  </Grid>
                </>
              )}

              <Grid item xs={12} sm={6}>
                <FormControl fullWidth variant="outlined" margin="normal">
                  <InputLabel>Account Status</InputLabel>
                  <Select
                    name="account_status"
                    label="Account Status"
                    value={formData.account_status}
                    onChange={handleInputChange}

                  >
                    {["Active", "Inactive"].map((status) => (
                      <MenuItem key={status} value={status}>{status}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={6}>
                <FormControl fullWidth variant="outlined" margin="normal">
                  <InputLabel>Industry</InputLabel>
                  <Select
                    name="industry"
                    label="Industry"
                    value={formData.industry}
                    onChange={handleInputChange}

                  >
                    <MenuItem value=""><em>None</em></MenuItem>
                    {[
                      "Automotive", "Banking", "Construction", "Education",
                      "Financial Services", "Government", "Healthcare", "Manufacturing",
                      "Media", "Retail", "Technology", "Travel", "Other"
                    ].map((industry) => (
                      <MenuItem key={industry} value={industry}>{industry}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              {/* <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  name="annual_revenue"
                  label="Annual Revenue"
                  variant="outlined"
                  margin="normal"
                  type="number"
                  onChange={handleInputChange}
                />
              </Grid> */}

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  name="annual_revenue"
                  label="Annual Revenue"
                  variant="outlined"
                  margin="normal"
                  type="number"
                  onChange={handleInputChange}
                  InputProps={{
                    startAdornment: currency && (
                      <InputAdornment position="start">
                        {currency.symbol || currency.code}
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  name="number_of_employees"
                  label="Number of Employees"
                  variant="outlined"
                  margin="normal"
                  type="number"
                  inputProps={{ min: 0 }}
                  onChange={handleInputChange}
                  error={!!formErrors.number_of_employees}
                  helperText={formErrors.number_of_employees}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <Autocomplete
                  options={currencies}
                  getOptionLabel={(option) => `${option.name} (${option.code})`}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Currency"
                      variant="outlined"
                      margin="normal"
                      fullWidth
                      required={isPartner}
                      error={!!formErrors.currency}
                      helperText={formErrors.currency}
                      InputLabelProps={{
                        ...params.InputLabelProps,
                        shrink: true,
                      }}
                    />
                  )}
                  value={currency}
                  onChange={(event, newValue) => {
                    handleCurrencyChange(event, newValue);
                    setFormErrors((prev) => ({ ...prev, currency: '' }));
                  }}
                  isOptionEqualToValue={(option, value) => option.code === value.code}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  name="website"
                  label="Website"
                  variant="outlined"
                  margin="normal"
                  onChange={handleInputChange}
                  error={!!formErrors.website}
                  helperText={formErrors.website}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  name="email"
                  label="Email"
                  variant="outlined"
                  margin="normal"
                  required={isPartner || (isClone && !phoneNational)}
                  value={formData.email || ''}
                  onChange={handleInputChange}
                  error={!!formErrors.email}
                  helperText={
                    formErrors.email ||
                    (isClone && !phoneNational
                      ? 'Enter a new email (or phone) — originals cannot be copied'
                      : undefined)
                  }
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <AccountPhoneInput
                  label="Phone"
                  required={isPartner || (isClone && !(formData.email || '').trim())}
                  dialCode={phoneDial}
                  national={phoneNational}
                  countryCodes={countryCodes}
                  getByDialCode={getByDialCode}
                  getPhoneLengthRange={getPhoneLengthRange}
                  error={formErrors.phone}
                  extraHelper={
                    isClone && !(formData.email || '').trim() && !phoneNational
                      ? 'Enter a new phone (or email) — originals cannot be copied'
                      : undefined
                  }
                  onDialChange={(next) => {
                    phoneDialTouched.current = true;
                    setPhoneDial(next);
                    const range = next ? getPhoneLengthRange({ dialCode: next }) : null;
                    if (range) setPhoneNational((current) => nationalDigits(current, range.maxLength));
                    setFormErrors((prev) => ({ ...prev, phone: '' }));
                  }}
                  onNationalChange={(next) => {
                    setPhoneNational(next);
                    setFormErrors((prev) => ({ ...prev, phone: '' }));
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <AccountPhoneInput
                  label="Mobile Number"
                  dialCode={mobileDial}
                  national={mobileNational}
                  countryCodes={countryCodes}
                  getByDialCode={getByDialCode}
                  getPhoneLengthRange={getPhoneLengthRange}
                  error={formErrors.mobile}
                  onDialChange={(next) => {
                    mobileDialTouched.current = true;
                    setMobileDial(next);
                    const range = next ? getPhoneLengthRange({ dialCode: next }) : null;
                    if (range) setMobileNational((current) => nationalDigits(current, range.maxLength));
                    setFormErrors((prev) => ({ ...prev, mobile: '' }));
                  }}
                  onNationalChange={(next) => {
                    setMobileNational(next);
                    setFormErrors((prev) => ({ ...prev, mobile: '' }));
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  name="fax"
                  label="Fax"
                  variant="outlined"
                  margin="normal"
                  onChange={handleInputChange}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  name="tax_id"
                  label="Tax Id"
                  variant="outlined"
                  margin="normal"
                  onChange={handleInputChange}
                />
              </Grid>

              {/* Billing Address Section */}
              <Grid item xs={12}>
                <MDTypography variant="h6" color="dark" mt={.5}>
                  Billing Address
                </MDTypography>
              </Grid>

              <Grid item xs={12}>
                <MDBox mt={2} mb={1}>
                  <AddressAutocompleteInput
                      label="Street Address"
                      placeholder="Enter street address"
                      value={formData.billing_address || ''}
                      onChange={(nextAddress) => {
                        setFormData((prev) => ({ ...prev, billing_address: nextAddress }));
                        setFormErrors((prev) => ({ ...prev, billing_address: '' }));
                      }}
                      error={Boolean(formErrors.billing_address)}
                      errorMessage={formErrors.billing_address}
                      required={isPartner}
                  />
                </MDBox>
              </Grid>

              <Grid item xs={12} sm={6}>
                <CitySelect
                  label="City"
                  country={formData.billing_country || ''}
                  state={formData.billing_state || ''}
                  value={formData.billing_city || ''}
                  margin="normal"
                  onChange={(nextCity) =>
                    setFormData((prev) => ({ ...prev, billing_city: nextCity || '' }))
                  }
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <StateSelect
                  label="State"
                  country={formData.billing_country || ''}
                  value={formData.billing_state || ''}
                  margin="normal"
                  onChange={(nextState) =>
                    setFormData((prev) => ({
                      ...prev,
                      billing_state: nextState || '',
                      billing_city: '',
                    }))
                  }
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  name="billing_stcd"
                  label="Billing State Code"
                  variant="outlined"
                  margin="normal"
                  onChange={handleInputChange}
                  inputProps={{
                    maxLength: 2,
                    pattern: "[0-9]*",
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <CountrySelect
                  label="Country"
                  value={formData.billing_country || ''}
                  margin="normal"
                  onChange={(nextCountry) => {
                    const dial = dialCodeForCountryName(nextCountry, countryCodes);
                    const range = dial ? getPhoneLengthRange({ dialCode: dial }) : null;
                    if (dial && !phoneDialTouched.current) {
                      setPhoneDial(dial);
                      if (range) setPhoneNational((current) => nationalDigits(current, range.maxLength));
                    }
                    if (dial && !mobileDialTouched.current) {
                      setMobileDial(dial);
                      if (range) setMobileNational((current) => nationalDigits(current, range.maxLength));
                    }
                    setFormData((prev) => ({
                      ...prev,
                      billing_country: nextCountry || '',
                      billing_state: '',
                      billing_city: '',
                    }));
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  name="billing_postal_code"
                  label="Postal Code"
                  variant="outlined"
                  margin="normal"
                  onChange={handleInputChange}
                />
              </Grid>

              {/* Shipping Address Section */}
              <Grid item xs={12}>
                <MDTypography variant="h6" color="dark" mt={.5}>
                  Shipping Address
                </MDTypography>
              </Grid>
              <Grid item xs={12}>
                {/* Checkbox to copy billing address */}
                <FormControlLabel
                  control={
                    <Checkbox
                      color="primary"
                      onChange={(event) => handleCopyBillingToShipping(event.target.checked)}
                    />
                  }
                  label="Shipping Address same as Billing Address"
                />
              </Grid>
              <Grid item xs={12}>
                <MDBox mt={2} mb={1}>
                  <AddressAutocompleteInput
                      label="Street Address"
                      placeholder="Enter street address"
                      value={formData.shipping_address || ''}
                      onChange={(nextAddress) =>
                          setFormData((prev) => ({ ...prev, shipping_address: nextAddress }))
                      }
                  />
                </MDBox>
              </Grid>

              <Grid item xs={12} sm={6}>
                <CitySelect
                  label="City"
                  country={formData.shipping_country || ''}
                  state={formData.shipping_state || ''}
                  value={formData.shipping_city || ''}
                  margin="normal"
                  onChange={(nextCity) =>
                    setFormData((prev) => ({ ...prev, shipping_city: nextCity || '' }))
                  }
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <StateSelect
                  label="State"
                  country={formData.shipping_country || ''}
                  value={formData.shipping_state || ''}
                  margin="normal"
                  onChange={(nextState) =>
                    setFormData((prev) => ({
                      ...prev,
                      shipping_state: nextState || '',
                      shipping_city: '',
                    }))
                  }
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  name="shipping_stcd"
                  label="Shipping State Code"
                  variant="outlined"
                  margin="normal"
                  value={formData.shipping_stcd || ''}
                  onChange={handleInputChange}
                  inputProps={{
                    maxLength: 2,
                    pattern: "[0-9]*",
                  }}
                  InputLabelProps={{
                    shrink: Boolean(formData.shipping_stcd),
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <CountrySelect
                  label="Country"
                  value={formData.shipping_country || ''}
                  margin="normal"
                  onChange={(nextCountry) =>
                    setFormData((prev) => ({
                      ...prev,
                      shipping_country: nextCountry || '',
                      shipping_state: '',
                      shipping_city: '',
                    }))
                  }
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  name="shipping_postal_code"
                  label="Postal Code"
                  variant="outlined"
                  margin="normal"
                  value={formData.shipping_postal_code || ''}
                  onChange={handleInputChange}
                  InputLabelProps={{
                    shrink: Boolean(formData.shipping_postal_code)
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <Autocomplete
                  options={managers}
                  getOptionLabel={(option) => option.username}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      fullWidth
                      label="Account Owner"
                      variant="outlined"
                      margin="normal"
                      InputLabelProps={{
                        ...params.InputLabelProps,
                        shrink: true,
                      }}
                    />
                  )}
                  value={selectedManager} // This will show the current user's username by default
                  onChange={(_, newValue) => setSelectedManager(newValue)} // Allow user to change it
                  isOptionEqualToValue={(option, value) => option?.id === value?.id}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  name="notes"
                  label="Notes"
                  variant="outlined"
                  margin="normal"
                  multiline
                  rows={3}
                  onChange={handleInputChange}
                />
              </Grid>

              {/* Add Custom Fields Section */}
              {customFields && customFields.length > 0 && (
                <Grid item xs={12}>
                  <MDBox mt={2}>
                    <MDTypography variant="h6" gutterBottom>
                      Custom Fields
                    </MDTypography>
                    <CustomFieldsSection
                      ref={customFieldsRef}
                      objectType="accounts"
                      objectData={formData}
                      isEditing={true}
                      onFieldUpdate={handleFieldUpdate}
                      onError={handleFieldError}
                      formErrors={formErrors}
                    />
                  </MDBox>
                </Grid>
              )}

              {/* Form Actions */}
              <Grid item xs={12}>
                <MDBox display="flex" justifyContent="flex-end" mt={3}>
                  <MDButton
                    variant="gradient"
                    color="secondary"
                    onClick={onClose}
                    sx={{ mr: 2 }}
                  >
                    Cancel
                  </MDButton>
                  <MDButton
                    variant="gradient"
                    color="info"
                    type="submit"
                    disabled={isSaving}
                  >
                    {isSaving ? (
                      <CircularProgress size={24} color="inherit" />
                    ) : (
                      'Save Account'
                    )}
                  </MDButton>
                </MDBox>
              </Grid>
            </Grid>
          </form>
        </Box>
      </Box>
    </Modal>
  );
};

export default AccountFormModal;